/* ============================================================================
   LA ENTRADA: QUIÉN VE QUÉ

   POR QUÉ ESTE MÓDULO EXISTE

   La entrada no es una pantalla: es una decisión. **El perfil con el que alguien
   entra decide qué aplicación ve**, y esa decisión tiene que estar escrita una
   sola vez, en el dominio, y no repartida entre el componente que dibuja la
   pantalla y el que dibuja el menú.

   ═══ DOS VISTAS, Y NADA EN EL MEDIO ═══

   · **Vuelo** — la vista de operación: planificar, calcular, volar, registrar.
     Es la que existe desde el principio, y es la del **piloto**.
   · **Gestión** — la vista de datos: horas, tableros, empresa, usuarios y
     clientes. Es la de **todos los demás perfiles**.

   Un perfil que no existe **no entra a ninguna de las dos**. No es una
   exageración: la vista de gestión muestra la operación de la empresa, y dársela
   a un perfil que no se reconoce es exactamente el error que un control de
   acceso tiene que evitar.

   ═══ EL MENÚ DE GESTIÓN SALE DE LA MATRIZ, NO DE UNA LISTA APARTE ═══

   Las secciones se filtran con `puede()`, la misma función que usa el servidor y
   que ya tiene sus pruebas. Escribir el menú a mano sería la segunda definición
   de «qué puede hacer este perfil», y dos definiciones del mismo dato terminan
   siempre en desacuerdo: alguien agrega un permiso, la matriz lo dice y el menú
   no se entera —o al revés, y el menú ofrece una puerta que el servidor va a
   cerrar con un 403 delante del usuario—.
   ========================================================================== */
import { perfilDe, puede } from "./identidad.js";

export const VISTAS = {
  VUELO: "vuelo",
  GESTION: "gestion",
};

/** El único perfil que opera. El resto administra datos. */
export const PERFIL_QUE_VUELA = "pilot";

/**
 * La vista que le corresponde a un perfil.
 *
 * Devuelve `null` para un perfil desconocido: quien llama decide qué hacer, pero
 * no se le da la vista de gestión por descarte. Un `?? GESTION` acá sería un
 * control de acceso que falla abierto.
 */
export function vistaDePerfil(perfil) {
  if (!perfilDe(perfil)) return null;
  return perfil === PERFIL_QUE_VUELA ? VISTAS.VUELO : VISTAS.GESTION;
}

/* ═══ LAS SECCIONES DE LA VISTA DE GESTIÓN ═══

   `panel` distingue las que YA tienen pantalla de las que todavía no. Las que no
   la tienen se declaran igual, a propósito: **la falta se ve en el código en vez
   de desaparecer**, que es como este proyecto encontró los diecinueve huérfanos.
   Y no se ofrece un botón que no lleva a ninguna parte. */
export const SECCIONES = [
  {
    clave: "tablero",
    nombre: "Tablero",
    permiso: "vuelos.leer",
    panel: true,
    descripcion: "Las horas del período, por aeronave, piloto, empresa y cliente.",
  },
  {
    clave: "estadisticas",
    nombre: "Horas de vuelo",
    permiso: "vuelos.leer",
    panel: true,
    descripcion: "Los cuatro cortes de las mismas horas, con rango de fechas y descarga en Excel.",
  },
  {
    clave: "empresa",
    nombre: "Empresa y usuarios",
    permiso: "empresa.leer",
    panel: true,
    descripcion: "Los datos de la empresa, sus usuarios, sus perfiles y sus clientes.",
  },
  {
    clave: "equipos",
    nombre: "Equipos y empresas",
    permiso: "vuelos.leer",
    panel: true,
    descripcion: "De qué empresa es cada equipo, desde cuándo y quién lo asignó.",
  },
  {
    clave: "facturacion",
    nombre: "Facturación",
    permiso: "facturacion.leer",
    panel: true,
    descripcion: "Horas por cliente por su tarifa, con la tarifa declarada y su procedencia.",
  },
  {
    clave: "mantenimiento",
    nombre: "Mantenimiento",
    permiso: "vuelos.leer",
    panel: false,
    descripcion: "El estado de célula y motor, los remanentes y las discrepancias de la bitácora.",
  },
  {
    clave: "auditoria",
    nombre: "Auditoría",
    permiso: "auditoria.leer",
    panel: false,
    descripcion: "El registro de lo que cambió, quién lo cambió y cuándo.",
  },
];

/**
 * Las secciones que le corresponden a un perfil, en el orden de la lista.
 *
 * `soloConPanel` deja pedir también las que todavía no tienen pantalla: eso es
 * lo que usa la prueba que vigila que la falta no se olvide.
 */
export function seccionesDe(perfil, { soloConPanel = true } = {}) {
  return SECCIONES.filter((s) => puede(perfil, s.permiso) && (!soloConPanel || s.panel));
}

/* ═══ UN PERFIL, UNA VISTA... Y UNA EXCEPCIÓN ═══

   El perfil decide con qué vista se ABRE, y eso no se toca: el piloto abre la
   operación, los demás abren la gestión.

   Pero un administrador de empresa **también vuela**. Su perfil lo deja en
   gestión, y la matriz le da `vuelos.escribir`: la vista de operación le
   corresponde por derecho y no tenía forma de abrirla. Sin esto quedaba
   encerrado en el tablero, sin poder abrir un plan de vuelo ni registrar uno.
   Lo encontró el operador usando su propio usuario.

   La excepción va en UNA sola dirección y la decide la matriz, no la pantalla:
   el botón es una invitación, el permiso es el que manda. Y `vuelos.escribir`
   —escribir, no leer— es el permiso correcto para preguntarlo: quien solo puede
   consultar horas no tiene nada que hacer en la vista de operación. */
export function vistasDe(perfil) {
  const propia = vistaDePerfil(perfil);
  if (propia === null) return [];
  if (propia === VISTAS.VUELO || !puede(perfil, "vuelos.escribir")) return [propia];
  return [propia, VISTAS.VUELO];
}

/** ¿Este perfil puede abrir esa vista? El botón se muestra con esto, y la
    pantalla vuelve a preguntarlo antes de cambiar: un botón es una invitación,
    no un permiso. */
export function puedeAbrirVista(perfil, vista) {
  return vistasDe(perfil).includes(vista);
}

/** La sección con la que abre la vista de gestión: la primera que puede ver. */
export function seccionInicial(perfil) {
  return seccionesDe(perfil)[0]?.clave || null;
}
